import React, { useEffect, useState, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { CheckCircle2, Rocket, ArrowRight, Loader2, Sparkles, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { brand } from '../../config/brand';
import { Badge } from '../../components/ui/badge';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

export default function BillingStatus(): JSX.Element {
    const history = useHistory();
    const { user, setUser } = useGlobalState();
    const [verifying, setVerifying] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [targetTier, setTargetTier] = useState<string | null>(null);
    const token = localStorage.getItem('token');

    // Detect target tier from intent before verifying
    useEffect(() => {
        const intent = localStorage.getItem('checkout_intent');
        if (intent) {
            // Map variant IDs to names
            const variants: Record<string, string> = {
                '1268199': 'BASIC',
                '1268184': 'BASIC',
                '1268203': brand.planNames.pro.toUpperCase(),
                '1268201': brand.planNames.pro.toUpperCase()
            };
            setTargetTier(variants[intent] || 'Upgrade');
        }
    }, []);

    const triggerSync = useCallback(async () => {
        if (!token || syncing) return;
        setSyncing(true);
        try {
            await axios.post(`${API_BASE_URL}/subscriptions/sync`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // After sync, the next /auth/me poll will catch the update
        } catch (error) {
            console.error("Manual sync failed:", error);
        } finally {
            setSyncing(false);
        }
    }, [token, syncing]);

    // Polling to check if the tier has updated in the backend
    useEffect(() => {
        if (!token) {
            history.push('/login');
            return;
        }

        let attempts = 0;
        const maxAttempts = 30; // 60 seconds total

        const checkStatus = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const updatedUser = response.data.user;
                if (updatedUser.tier !== 'FREE') {
                    setUser(updatedUser);
                    setVerifying(false);
                    localStorage.removeItem('checkout_intent');
                    return true;
                }

                // If after 5 attempts (10 seconds) it's still FREE, trigger a backend sync automatically
                if (attempts === 5) {
                    triggerSync();
                }

                if (attempts >= maxAttempts) {
                    setVerifying(false);
                    return true;
                }
            } catch (error) {
                console.error("Error verifying billing status:", error);
            }
            return false;
        };

        const interval = setInterval(async () => {
            attempts++;
            const done = await checkStatus();
            if (done) clearInterval(interval);
        }, 2000);

        // Initial check
        checkStatus();

        return () => clearInterval(interval);
    }, [token, history, setUser, triggerSync]);

    const isPaid = user?.tier !== 'FREE';

    const handleManualRefresh = async () => {
        await triggerSync();
        // Wait a small bit and then reload or let the effect handle it
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4 overflow-hidden relative">
            {/* Background blobs */}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-full animate-pulse" />
            <div className="absolute bottom-[10%] right-[-10%] w-[30%] h-[30%] bg-indigo-600/10 blur-[120px] rounded-full animate-pulse delay-700" />

            <Card className="w-full max-w-lg bg-card/50 border-border backdrop-blur-xl rounded-[40px] shadow-2xl relative z-10 overflow-hidden">
                {isPaid && (
                    <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary via-indigo-500 to-purple-500 animate-pulse" />
                )}

                <CardHeader className="pt-12 text-center">
                    <div className="flex justify-center mb-6">
                        {verifying ? (
                            <div className="relative">
                                <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full animate-pulse" />
                                <Loader2 className="h-20 w-20 text-primary animate-spin relative z-10" />
                            </div>
                        ) : isPaid ? (
                            <div className="relative group">
                                <div className="absolute inset-0 bg-emerald-500/20 blur-2xl rounded-full group-hover:scale-150 transition-transform duration-1000" />
                                <div className="h-20 w-20 bg-emerald-500 rounded-3xl flex items-center justify-center shadow-lg shadow-emerald-500/20 relative z-10 transform rotate-12 group-hover:rotate-0 transition-transform duration-500">
                                    <CheckCircle2 className="h-10 w-10 text-white" />
                                </div>
                            </div>
                        ) : (
                            <div className="relative">
                                <div className="absolute inset-0 bg-red-500/10 blur-2xl rounded-full" />
                                <div className="h-20 w-20 bg-card rounded-3xl flex items-center justify-center border border-border relative z-10">
                                    <span className="text-4xl text-muted-foreground">⏳</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <CardTitle className="text-4xl font-black text-foreground italic uppercase tracking-tighter mb-2">
                        {verifying ? "Confirming Payment" : isPaid ? "Payment Successful!" : "Syncing Tiers..."}
                    </CardTitle>
                    <div className="text-muted-foreground font-medium px-6">
                        {verifying
                            ? "We are verifying your transaction. This usually takes 10-30 seconds."
                            : isPaid
                                ? `Experience the full power of ${brand.name}. Your account has been upgraded.`
                                : "The payment was received but your account tier is taking a bit longer to sync."}
                    </div>
                </CardHeader>

                <CardContent className="px-10 py-6">
                    <div className="bg-muted/50 rounded-3xl p-6 border border-border space-y-4">
                        <div className="flex justify-between items-center text-sm border-b border-border pb-3">
                            <span className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Purchase Status</span>
                            <Badge className={isPaid ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-primary/10 text-primary border-primary/20"}>
                                {isPaid ? "SUBSCRIPTION ACTIVE" : "PAYMENT RECEIVED"}
                            </Badge>
                        </div>

                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Plan Acquired</span>
                            <div className="flex items-center gap-2">
                                {(isPaid ? user?.tier : targetTier) === 'PRO' && <Zap className="h-3.5 w-3.5 text-primary fill-primary" />}
                                {(isPaid ? user?.tier : targetTier) === 'BASIC' && <Rocket className="h-3.5 w-3.5 text-indigo-400" />}
                                <span className="text-foreground font-black uppercase tracking-tight italic">
                                    {((isPaid ? user?.tier : targetTier) === 'PRO' ? brand.planNames.pro.toUpperCase() : (isPaid ? user?.tier : targetTier)) || 'Processing...'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {isPaid && (
                        <div className="mt-8 flex items-start gap-4 p-4 bg-primary/5 rounded-2xl border border-primary/10">
                            <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                            <div className="space-y-1">
                                <p className="text-sm font-bold text-primary">Upgrade Verified!</p>
                                <p className="text-xs text-muted-foreground leading-relaxed font-medium">
                                    Your new limits are now active. All features have been unlocked.
                                </p>
                            </div>
                        </div>
                    )}
                </CardContent>

                <CardFooter className="p-10 pt-4 flex flex-col gap-4">
                    <Button
                        asChild
                        disabled={!isPaid}
                        className={`w-full h-16 rounded-[24px] font-black uppercase tracking-widest text-sm transition-all shadow-xl ${isPaid ? 'bg-primary hover:bg-primary/90 text-white shadow-primary/20 active:scale-95' : 'bg-card text-muted-foreground pointer-events-none opacity-50'} group`}
                    >
                        <Link to="/dashboard" className="flex items-center justify-center gap-2">
                            <span>{isPaid ? "Go to Dashboard" : "Waiting for Activation..."}</span>
                            {isPaid && <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                        </Link>
                    </Button>

                    {!isPaid && !verifying && (
                        <div className="w-full space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-700">
                            <Button
                                variant="outline"
                                onClick={handleManualRefresh}
                                disabled={syncing}
                                className="w-full border-border bg-muted/50 text-foreground h-12 rounded-xl"
                            >
                                {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Loader2 className="mr-2 h-4 w-4" />}
                                {syncing ? "Syncing..." : "Try Manual Sync & Refresh"}
                            </Button>
                            <p className="text-[10px] text-muted-foreground text-center font-medium leading-relaxed">
                                Still nothing? Changing plans during checkout can sometimes delay the sync.
                                <br />
                                <button
                                    onClick={() => (window as any).openSupport?.('Billing Issue')}
                                    className="text-primary hover:underline font-bold"
                                >
                                    Contact Support
                                </button> and we'll fix it immediately.
                            </p>
                        </div>
                    )}
                </CardFooter>
            </Card>

            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none -z-0 opacity-[0.03] select-none">
                <span className="text-[20vw] font-black italic uppercase leading-none">SUCCESS</span>
            </div>
        </div>
    );
}
